import { Breadcrumb, Card, Col, Row, Space, Tag, Typography, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import * as APITypes from "~/API";
import {
  LISTING_FEATURE_KEYS,
  LISTING_FEATURE_LABEL_KEYS,
  type ListingDetailsResult,
} from "~/types/listing";
import { dateToLocalTime, getRouteWithParams, ROUTES } from "~/utils";
import { formatListingRent } from "~/utils/formatListingRent";
import {
  breadcrumbHomeItem,
  breadcrumbItem,
  breadcrumbLinkItem,
  CoverImageGallery,
} from "~/components";
import { PlanAvailableUnitsTable } from "./PlanAvailableUnitsTable";

function formatLocation(property: ListingDetailsResult["property"]): string {
  const parts = [
    property.city,
    property.state,
    property.country,
    property.zipCode,
  ].filter(Boolean);
  return parts.join(", ");
}

export function ListingDetailsView({ details }: { details: ListingDetailsResult }) {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const applyDisabled =
    details.tenant.status === APITypes.TenantStatusType.Active;

  const priceLine =
    details.kind === "plan"
      ? details.rentMin === details.rentMax
        ? formatListingRent(details.rentMin)
        : t("Listing_price_from", { amount: formatListingRent(details.rentMin) })
      : formatListingRent(details.rent);

  const metaLine =
    details.kind === "plan"
      ? t("Listing_plan_meta", {
          count: details.availableUnits.length,
          beds: details.numBedroom,
          baths: details.numBathroom,
        })
      : [
          t("Listing_unit_meta_beds_baths", {
            beds: details.numBedroom,
            baths: details.numBathroom,
          }),
          details.size
            ? t("Listing_unit_meta_size", { size: details.size })
            : null,
        ]
          .filter(Boolean)
          .join(" · ");

  const activeFeatures = LISTING_FEATURE_KEYS.filter(
    (key) => details.features[key] === true
  );

  return (
    <>
      <Breadcrumb
        separator=">"
        items={[
          breadcrumbHomeItem,
          breadcrumbLinkItem(ROUTES.unit, t("Listing_browse_title")),
          breadcrumbItem(details.name),
        ]}
      />

      <section style={{ marginTop: "2rem" }}>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={14}>
          <CoverImageGallery imageUrls={details.imageUrls} title={details.name} />
        </Col>
        <Col xs={24} lg={10}>
          <Typography.Title level={2} style={{ marginTop: 0 }}>
            {details.name}
          </Typography.Title>
          <Typography.Text type="secondary">
            {details.property.name} · {formatLocation(details.property)}
          </Typography.Text>
          <div style={{ marginTop: 4 }}>
            <Button
              type="link"
              style={{ padding: 0, height: "auto" }}
              onClick={() =>
                navigate(
                  getRouteWithParams("propertyDetails", {
                    propertyID: details.property.id,
                  })
                )
              }
            >
              {t("Listing_view_property_listings")}
            </Button>
          </div>
          <Typography.Title level={3} style={{ margin: "12px 0 4px" }}>
            {priceLine}
          </Typography.Title>
          <Typography.Text>{metaLine}</Typography.Text>

          {details.kind === "unit" && details.availabilityDate ? (
            <Typography.Paragraph style={{ marginTop: 8 }}>
              {t("Date_Of_Availability")}:{" "}
              {dateToLocalTime(details.availabilityDate, "dateTime")}
            </Typography.Paragraph>
          ) : null}

          {activeFeatures.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 16,
              }}
            >
              {activeFeatures.map((key) => (
                <Tag key={key}>{t(LISTING_FEATURE_LABEL_KEYS[key])}</Tag>
              ))}
            </div>
          ) : null}

          {details.kind === "unit" ? (
            <Space style={{ marginTop: 16 }}>
              <Button
                type="primary"
                disabled={applyDisabled}
                onClick={() =>
                  navigate(
                    getRouteWithParams("leaseApplication", {
                      entityType: "Unit",
                      entityID: details.id,
                    })
                  )
                }
              >
                {t("Apply_for_Lease")}
              </Button>
            </Space>
          ) : null}
        </Col>
      </Row>

      {details.description ? (
        <section style={{ marginTop: 24 }}>
          <Typography.Title level={4}>{t("Description")}</Typography.Title>
          <Typography.Paragraph>{details.description}</Typography.Paragraph>
        </section>
      ) : null}

      {details.property.description ? (
        <section style={{ marginTop: 16 }}>
          <Typography.Title level={5}>
            {t("Listing_details_property_title")}
          </Typography.Title>
          <Typography.Paragraph>{details.property.description}</Typography.Paragraph>
        </section>
      ) : null}

      {details.amenities.length > 0 ? (
        <section style={{ marginTop: 24 }}>
          <Typography.Title level={4}>
            {t("Listing_details_amenities_title")}
          </Typography.Title>
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            {details.amenities.map((amenity) => (
              <Col key={amenity.id} xs={24} sm={12} md={8}>
                <Card size="small" title={amenity.name}>
                  {amenity.locationDescription ? (
                    <Typography.Text type="secondary">
                      {amenity.locationDescription}
                    </Typography.Text>
                  ) : null}
                  {amenity.description ? (
                    <Typography.Paragraph
                      style={{ marginTop: 8, marginBottom: 0 }}
                    >
                      {amenity.description}
                    </Typography.Paragraph>
                  ) : null}
                </Card>
              </Col>
            ))}
          </Row>
        </section>
      ) : null}

      {details.kind === "plan" ? (
        <PlanAvailableUnitsTable
          units={details.availableUnits}
          tenant={details.tenant}
        />
      ) : null}
      </section>
    </>
  );
}
